import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useLabels } from 'wsm-common-data';
import { setClassNames } from '@ddc/classnames-js';
import { checkIfOpen } from '../utils/checkIfOpen';
import { getNextTimeEvent } from '../utils/getNextTimeEvent';
import { toShortDay } from '../utils/toDayShort';
import HoursModal from './HoursModal';

const LocationHours = ({ hours, timeZoneId }) => {
	const labels = useLabels();
	const [mounted, setIsMounted] = useState(null);

	const [showModal, setShowModal] = useState(false);
	const handleShowModal = () => {
		setShowModal(true);
	};

	useEffect(() => {
		setIsMounted(true);
		return () => setIsMounted(false);
	}, []);

	const now = new Date();
	const currentDay = now
		.toLocaleString('en-us', {
			weekday: 'long',
			timeZone: timeZoneId
		})
		.toLowerCase();
	const currentTime = now.toLocaleTimeString('en-us', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: true,
		timeZone: timeZoneId
	});
	// Check if the location is open now
	const isOpen = checkIfOpen(
		hours,
		currentDay,
		currentTime.replace(/\s/g, '')
	);
	const { statement, time, nextDay } = getNextTimeEvent(
		hours,
		currentDay,
		currentTime.replace(/\s/g, '')
	);
	if (!mounted) {
		return null;
	}
	let openCloseStatement = '';
	if (nextDay) {
		const nextDayValue = labels.get(nextDay.toUpperCase());
		openCloseStatement = `${labels.get(statement)} ${time} ${toShortDay(
			nextDayValue
		)}`;
	} else if (statement && time) {
		openCloseStatement = `${labels.get(statement)} ${time}`;
	}

	const openCloseClasses = setClassNames([
		'font-weight-bold',
		isOpen ? 'text-success' : 'text-danger'
	]);
	return (
		<section
			className="location-hours d-flex py-4 border-top"
			aria-label="Business hours information"
		>
			<div className="pl-3">
				<div
					role="status"
					aria-live="polite"
					aria-label={`Current status: ${
						isOpen ? 'Open' : 'Closed'
					}. ${openCloseStatement}`}
				>
					<p className="m-0">
						<span
							className={openCloseClasses}
							aria-label={`Currently ${
								isOpen ? 'open' : 'closed'
							}`}
						>
							{isOpen
								? labels.get('OPEN').toUpperCase()
								: labels.get('CLOSED').toUpperCase()}
						</span>
						{' ⋅ '}
						<span
							className="label-status text-muted"
							aria-label={`Hours information: ${openCloseStatement}`}
						>
							{openCloseStatement}
						</span>
					</p>
				</div>
				<button
					type="button"
					className="btn-unstyled p-0 text-left"
					aria-label={`${labels.get(
						'VIEW_ALL_HOURS'
					)} - Click to view detailed business hours`}
					title={`${labels.get(
						'VIEW_ALL_HOURS'
					)} - Click to view detailed business hours`}
					onClick={handleShowModal}
				>
					<p className="m-0">{labels.get('VIEW_ALL_HOURS')}</p>
				</button>
			</div>
			<div className="ml-auto d-flex align-items-center justify-content-center text-primary">
				<button
					type="button"
					className="btn-unstyled"
					aria-label="View detailed business hours"
					title="View detailed business hours"
					onClick={handleShowModal}
				>
					<i
						className="ddc-icon ddc-icon-chevron-right ddc-icon-size-medium"
						aria-hidden="true"
					/>
					<span className="sr-only">
						{labels.get('EXPAND_HOURS')}
					</span>
				</button>
			</div>
			<HoursModal show={showModal} onHide={() => setShowModal(false)} />
		</section>
	);
};

LocationHours.propTypes = {
	hours: PropTypes.shape({
		monday: PropTypes.string.isRequired,
		tuesday: PropTypes.string.isRequired,
		wednesday: PropTypes.string.isRequired,
		thursday: PropTypes.string.isRequired,
		friday: PropTypes.string.isRequired,
		saturday: PropTypes.string.isRequired,
		sunday: PropTypes.string.isRequired
	}).isRequired,
	timeZoneId: PropTypes.string.isRequired
};

export default LocationHours;
